import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { database } from '../../../utils/firebase';
import './ReportGenerator.css';
import {
    Breadcrumbs,
    Button,
    Link,
    RadioGroup,
    Select,
    Sheet,
    Typography,
    Option,
    FormControl,
    FormLabel, Switch, Stack
} from "@mui/joy";
import {CheckCircleRounded, Print} from "@mui/icons-material"; // Assuming you have a CSS file for additional styling
import Radio, { radioClasses } from '@mui/joy/Radio';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";

const dataSources = {
    payouts: {
        name: 'Payouts',
        reports: [
            { id: 'payoutsReport', name: 'Payouts Report', description: 'Detailed report of payouts', image: 'https://res.cloudinary.com/ds5afrz85/image/upload/v1741035333/localhost_2025-03-03_18_02_37_bxcgrd.png' }
        ]
    },
    matches: {
        name: 'Matches',
        reports: [
            { id: 'matchReport', name: 'Match Report', description: 'Detailed report of match scores', image: 'https://res.cloudinary.com/ds5afrz85/image/upload/v1741035318/matchRept_dbwzqv.png' },
            { id: 'winnersReport', name: 'Winners Report', description: 'Detailed report of winners of a match', image: 'https://res.cloudinary.com/ds5afrz85/image/upload/v1741035315/winnerRept_rd7g7y.png' },
            { id: 'blankScoreSheet', name: 'Blank Score Sheet', description: 'Blank score sheet for matches', image: 'https://res.cloudinary.com/ds5afrz85/image/upload/v1741035431/localhost_2025-03-03_18_10_20_p0b0ge.png' },
            { id: 'matchSchedule', name: 'Match Schedule', description: 'Schedule of upcoming matches', image: 'https://res.cloudinary.com/ds5afrz85/image/upload/v1741035306/matchSched_gmkucm.png' }
        ]
    },
    roster: {
        name: 'Roster',
        reports: [
            { id: 'rosterReport', name: 'Roster Report', description: 'Detailed report of roster', image: 'https://res.cloudinary.com/ds5afrz85/image/upload/v1741035343/localhost_2025-03-03_17_47_09_kp6kbe.png' }
        ]
    },
    handicaps: {
        name: 'Handicaps',
        reports: [
            { id: 'handicapReport', name: 'Handicap Report', description: 'Detailed report of handicaps', image: 'https://res.cloudinary.com/ds5afrz85/image/upload/v1741035439/localhost_2025-03-03_18_11_23_jywibz.png' }
        ]
    },
    // Add more reports as needed
};

const ReportGenerator = () => {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const [selectedDataSource, setSelectedDataSource] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [settings, setSettings] = useState({});
    const [matches, setMatches] = useState([]);
    const [handicaps, setHandicaps] = useState([]);
    const [print, setPrint] = useState(false);
    const [leagueData, setLeagueData] = useState({});

    useEffect(() => {
        const leagueRef = ref(database, `leagues/${leagueId}`);
        onValue(leagueRef, (snapshot) => {
            setLeagueData(snapshot.val());
        })
        },
    [leagueId]);

    useEffect(() => {
        if (selectedReport === 'matchReport' || selectedReport === 'blankScoreSheet') {
            const matchesRef = ref(database, `leagues/${leagueId}/matches`);
            onValue(matchesRef, (snapshot) => {
                const data = snapshot.val();
                setMatches(data ? Object.entries(data).map(([id, match]) => ({ id, ...match })) : []);
            });
        } else if (selectedReport === 'handicapReport') {
            const handicapsRef = ref(database, `leagues/${leagueId}/handicaps`);
            onValue(handicapsRef, (snapshot) => {
                const data = snapshot.val();
                setHandicaps(Object.keys(data).map(key => ({ id: key, name: data[key].name })));
            });
        }
    }, [selectedReport, leagueId]);

    const handleDataSourceChange = (dataSource) => {
        setSelectedDataSource(dataSource);
        setSelectedReport(null);
        setSettings({});
    };

    const handleGenerateReport = () => {
        let url = `/utils/league/reports/${selectedReport}/${leagueId}`;
        if (selectedReport === 'payoutsReport' && settings.startDate && settings.endDate) {
            url += `/${Math.floor(settings.startDate.unix())}`;
            url += `/${Math.floor(settings.endDate.unix())}`;
        } else if ((selectedReport === 'matchReport' || selectedReport === 'winnersReport' || selectedReport === 'blankScoreSheet') && settings.matchId) {
            url += `/${settings.matchId}`;
        } else if (selectedReport === 'matchSchedule' && settings.startDate && settings.endDate) {
            url += `/${Math.floor(settings.startDate.unix())}`;
            url += `/${Math.floor(settings.endDate.unix())}`;
        } else if (selectedReport === 'rosterReport' && settings.activeDate) {
            url += `/${Math.floor(settings.activeDate.unix()*1000)}`;
        } else if (selectedReport === 'handicapReport' && settings.handicapId) {
            url += `/${settings.handicapId}`;
        }

        if (print) url += '/true';
        if (!print) url += '/false';
        if (settings.showHeader) url += '/true';
        if (!settings.showHeader) url += '/false';
        window.open(url, '_blank');
    };

    const formatDate = (unixTime) => {
        const date = new Date(unixTime * 1000);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <div className="container">
            <Breadcrumbs aria-label="breadcrumb">
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/dashboard`)}>
                    {leagueData.name}
                </Link>
                <Typography sx={{ color: 'text.primary' }}>Reports</Typography>
            </Breadcrumbs>
            <h1>Report Generation Wizard</h1>
            <div className="dashboard" style={{overflow: 'visible'}}>
                <Stack spacing={4}>
                    <div style={{width: '250px'}}>
                        <h2>Select Data Source</h2>
                        <Select
                            onChange={(e, newValue) => handleDataSourceChange(newValue)}
                            className="styled-select"
                            placeholder={"Select a data source"}
                        >
                            {Object.keys(dataSources).map(key => (
                                <Option key={key} value={key}>{dataSources[key].name}</Option>
                            ))}
                        </Select>
                    </div>
                    {selectedDataSource && (
                        <div style={{marginTop: '0px'}}>
                            <h2>Select Report</h2>
                            <RadioGroup
                                aria-label="platform"
                                defaultValue="Website"
                                overlay
                                name="platform"
                                value={selectedReport}
                                onChange={(e) => setSelectedReport(e.target.value)}
                                sx={{
                                    flexDirection: 'row',
                                    gap: 2,
                                    [`& .${radioClasses.checked}`]: {
                                        [`& .${radioClasses.action}`]: {
                                            inset: -1,
                                            border: '3px solid',
                                            borderColor: 'primary.500',
                                        },
                                    },
                                    [`& .${radioClasses.radio}`]: {
                                        display: 'contents',
                                        '& > svg': {
                                            zIndex: 2,
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            bgcolor: 'background.surface',
                                            borderRadius: '50%',
                                        },
                                    },
                                }}
                            >
                                {dataSources[selectedDataSource].reports.map(report => (
                                    <Sheet
                                        key={report.id}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 'md',
                                            boxShadow: 'sm',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 2,
                                            minWidth: 120,
                                            overflow: 'visible', // Allow hover effect to expand outside the container
                                        }}
                                    >
                                        <Radio id={report.id} value={report.id} checkedIcon={<CheckCircleRounded />} />
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                                            <div style={{ width: '150px', height: '100px' }}>
                                                <img src={report.image} alt={report.name} className="report-image" />
                                            </div>
                                        </div>
                                        <h3>{report.name}</h3>
                                        <p>{report.description}</p>
                                    </Sheet>
                                ))}
                            </RadioGroup>
                        </div>
                    )}
                    {selectedReport && (
                        <Stack spacing={2} style={{width: "250px"}}>
                            <h2>Report Settings</h2>
                            {selectedReport === 'matchReport' || selectedReport === 'winnersReport' || selectedReport === 'blankScoreSheet' ? (
                                <div style={{display: 'flex', flexDirection: 'column', width: '250px'}}>
                                    <FormControl>
                                        <FormLabel>Match</FormLabel>
                                        <Select
                                            onChange={(e, newValue) => setSettings({ ...settings, matchId: newValue })}
                                            className="styled-select"
                                            placeholder={"Select a match"}
                                        >
                                            {/*<option value="">Select a match</option>*/}
                                            {matches.map(match => (
                                                <Option key={match.id} value={match.id}>{formatDate(match.dateTime)}</Option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            ) : null}
                            {selectedReport === 'payoutsReport' || selectedReport === 'matchSchedule' ? (
                                <div style={{display: 'flex', flexDirection: 'column', width: '250px'}}>
                                    <FormControl>
                                        <FormLabel>Start Date</FormLabel>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                value={settings.startDate}
                                                onChange={(date) => setSettings({ ...settings, startDate: date })} className="styled-select" />
                                        </LocalizationProvider>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>End Date</FormLabel>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                value={settings.endDate}
                                                onChange={(date) => setSettings({ ...settings, endDate: date })} className="styled-select" />
                                        </LocalizationProvider>
                                    </FormControl>

                                </div>
                            ) : null}
                            {selectedReport === 'handicapReport' ? (
                                <div style={{display: 'flex', flexDirection: 'column', width: '250px'}}>
                                    <FormControl>
                                        <FormLabel>Handicap Set</FormLabel>
                                        <Select
                                            onChange={(e, newValue) => setSettings({ ...settings, handicapId: newValue })}
                                            className="styled-select"
                                            placeholder={"Select a handicap set"}
                                        >
                                            {handicaps.map(handicap => (
                                                <Option key={handicap.id} value={handicap.id}>{handicap.name}</Option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            ) : null}
                            {selectedReport === 'rosterReport' ? (
                                <div style={{display: 'flex', flexDirection: 'column', width: '250px'}}>
                                    <FormControl>
                                        <FormLabel>Active Date</FormLabel>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                value={settings.activeDate}
                                                onChange={(date) => setSettings({ ...settings, activeDate: date })}
                                                className="styled-select" />
                                        </LocalizationProvider>
                                    </FormControl>
                                </div>
                            ) : null}
                            <FormControl
                                orientation="horizontal"
                                sx={{ justifyContent: 'space-between' }}
                            >
                                <FormLabel>Print</FormLabel>
                                <Switch
                                    value={print}
                                    onChange={(e) => setPrint(e.target.checked)}
                                    color={print ? 'success' : 'neutral'}
                                    variant={print ? 'solid' : 'outlined'}
                                    endDecorator={print ? 'On' : 'Off'}
                                    slotProps={{
                                        endDecorator: {
                                            sx: {
                                                minWidth: 24,
                                            },
                                        },
                                    }}
                                />
                            </FormControl>
                            <FormControl
                                orientation="horizontal"
                                sx={{ justifyContent: 'space-between' }}
                            >
                                <FormLabel>Show Header</FormLabel>
                                <Switch
                                    value={settings.showHeader}
                                    onChange={(e) => setSettings({ ...settings, showHeader: e.target.checked })}
                                    color={settings.showHeader ? 'success' : 'neutral'}
                                    variant={settings.showHeader ? 'solid' : 'outlined'}
                                    endDecorator={settings.showHeader ? 'On' : 'Off'}
                                    slotProps={{
                                        endDecorator: {
                                            sx: {
                                                minWidth: 24,
                                            },
                                        },
                                    }}
                                />
                            </FormControl>
                            <Button
                                onClick={handleGenerateReport}
                                className="button-gradient"
                                style={{
                                    width: "auto",
                                    maxWidth: "100%",
                                    boxSizing: "border-box",
                                    flexShrink: 0, // Prevents the button from stretching
                                    alignSelf: "flex-start", // Ensures it aligns to its content
                                }}
                                startDecorator={<Print />}
                            >
                                Generate Report
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </div>
        </div>
    );
};

export default ReportGenerator;